/**
 * Comprehensive Error Tracking with Sentry
 * Captures frontend errors, API errors, and user context
 */

import * as Sentry from "@sentry/react";
import { getEnvironment } from "./api-config";

/**
 * Initialize Sentry error tracking
 */
export function initializeErrorTracking() {
  const environment = getEnvironment();
  const isDev = environment === "development";

  // Sentry DSN - use environment variable (Doppler) or fallback
  const sentryDsn =
    import.meta.env.VITE_SENTRY_DSN ||
    import.meta.env.VITE_SENTRY_DSN_FRONTEND ||
    "https://845434b1187a091c6171468c36d9641b@o4510864341860352.ingest.de.sentry.io/4510864358244432";

  Sentry.init({
    dsn: sentryDsn,
    environment,

    // Send default PII data (IP addresses, etc.)
    sendDefaultPii: true,

    // Enable tracing for performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: isDev ? 1.0 : 0.1,
    tracePropagationTargets: ["localhost", /^https:\/\/c\.swisstax\.work\/api/],

    // Session Replay
    replaysSessionSampleRate: isDev ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0, // 100% of errors

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || "development",

    // Ignore common browser errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "atomicFindClose",
      // Network errors
      "NetworkError",
      "Network request failed",
      // ResizeObserver
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],

    // Add breadcrumbs for debugging
    beforeBreadcrumb(breadcrumb, hint) {
      // Don't capture too much data
      if (breadcrumb.category === "console" && breadcrumb.level !== "error") {
        return null;
      }

      // Sanitize data
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeData(breadcrumb.data);
      }

      return breadcrumb;
    },

    // Modify events before sending
    beforeSend(event, hint) {
      // Add custom context
      event.tags = {
        ...event.tags,
        region: "europe-west6", // Swiss data center
        platform: "firebase",
      };

      // Sanitize sensitive data
      if (event.request) {
        event.request = sanitizeRequest(event.request);
      }

      // Log to console in development
      if (isDev) {
        console.error("[Sentry Event]", event, hint);
      }

      return event;
    },
  });

  console.log("[Error Tracking] Sentry initialized:", {
    environment,
    release: Sentry.getCurrentScope().getClient()?.getOptions().release,
  });
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: {
  id: string | number;
  email?: string;
  name?: string;
  companyId?: string | number;
}) {
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.name,
    company_id: user.companyId ? String(user.companyId) : undefined,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, any>,
  level: "debug" | "info" | "warning" | "error" = "info"
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data: data ? sanitizeData(data) : undefined,
    level,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture exception with context
 */
export function captureException(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: "fatal" | "error" | "warning" | "info" | "debug";
  }
) {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra ? sanitizeData(context.extra) : undefined,
    level: context?.level,
  });

  // Also log to console
  console.error("[Error Captured]", error, context);
}

/**
 * Capture message (for non-exception errors)
 */
export function captureMessage(
  message: string,
  level: "fatal" | "error" | "warning" | "info" | "debug" = "info",
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }
) {
  Sentry.captureMessage(message, {
    level,
    tags: context?.tags,
    extra: context?.extra ? sanitizeData(context.extra) : undefined,
  });
}

/**
 * Track tRPC errors with full context
 */
export function captureApiError(
  error: Error,
  context: {
    procedure: string;
    input?: any;
    path?: string;
    method?: string;
  }
) {
  addBreadcrumb(
    `API Error: ${context.procedure}`,
    "api",
    {
      procedure: context.procedure,
      path: context.path,
      method: context.method,
    },
    "error"
  );

  captureException(error, {
    tags: {
      error_type: "api_error",
      procedure: context.procedure,
      method: context.method || "unknown",
    },
    extra: {
      procedure: context.procedure,
      input: context.input ? sanitizeData(context.input) : undefined,
      path: context.path,
    },
    level: "error",
  });
}

/**
 * Track component errors
 */
export function captureComponentError(
  error: Error,
  componentName: string,
  componentStack?: string
) {
  addBreadcrumb(
    `Component Error: ${componentName}`,
    "ui",
    { component: componentName },
    "error"
  );

  captureException(error, {
    tags: {
      error_type: "component_error",
      component: componentName,
    },
    extra: {
      component_stack: componentStack,
    },
    level: "error",
  });
}

/**
 * Sanitize sensitive data before sending to Sentry
 */
function sanitizeData(data: any): any {
  if (!data) return data;

  const sensitiveKeys = [
    "password",
    "token",
    "secret",
    "apiKey",
    "accessToken",
    "refreshToken",
    "creditCard",
    "ssn",
    "taxId",
    "bankAccount",
  ];

  if (typeof data === "object") {
    const sanitized: any = Array.isArray(data) ? [] : {};

    for (const key in data) {
      if (
        sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))
      ) {
        sanitized[key] = "[REDACTED]";
      } else if (typeof data[key] === "object") {
        sanitized[key] = sanitizeData(data[key]);
      } else {
        sanitized[key] = data[key];
      }
    }

    return sanitized;
  }

  return data;
}

/**
 * Sanitize HTTP request data
 */
function sanitizeRequest(request: any): any {
  if (!request) return request;

  return {
    ...request,
    cookies: "[REDACTED]",
    headers: {
      ...request.headers,
      authorization: request.headers?.authorization ? "[REDACTED]" : undefined,
      cookie: "[REDACTED]",
    },
    data: request.data ? sanitizeData(request.data) : undefined,
  };
}

/**
 * Start a transaction for performance monitoring
 */
export function startTransaction(name: string, op: string = "http.request") {
  return Sentry.startSpan(
    {
      name,
      op,
    },
    span => span
  );
}

/**
 * Create error context for specific features
 */
export const errorContext = {
  /**
   * Bank import errors
   */
  bankImport: (error: Error, fileName: string, rowCount?: number) => {
    captureException(error, {
      tags: {
        feature: "bank_import",
        file_name: fileName,
      },
      extra: {
        row_count: rowCount,
      },
      level: "error",
    });
  },

  /**
   * Tax calculation errors
   */
  taxCalculation: (error: Error, taxType: string, period: string) => {
    captureException(error, {
      tags: {
        feature: "tax_calculation",
        tax_type: taxType,
        period,
      },
      level: "error",
    });
  },

  /**
   * Document processing errors
   */
  documentProcessing: (
    error: Error,
    documentId: string,
    documentType: string
  ) => {
    captureException(error, {
      tags: {
        feature: "document_processing",
        document_type: documentType,
      },
      extra: {
        document_id: documentId,
      },
      level: "error",
    });
  },

  /**
   * Reconciliation errors
   */
  reconciliation: (error: Error, transactionCount: number) => {
    captureException(error, {
      tags: {
        feature: "reconciliation",
      },
      extra: {
        transaction_count: transactionCount,
      },
      level: "error",
    });
  },
};
